import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for standardized CRUD form operations.
 * Extracts common state management and handlers from form components,
 * reducing boilerplate by ~70% across BusinessForm, VendorForm, CategoryForm,
 * SKUForm, and BudgetForm.
 *
 * @param {Object} config - Hook configuration
 * @param {Function} config.apiGetAll - API function to fetch all items
 * @param {Function} config.apiCreate - API function to create an item
 * @param {Function} config.apiUpdate - API function to update an item
 * @param {Function} config.apiDelete - API function to delete an item
 * @param {Function} [config.apiToggleStatus] - API function to toggle item status
 * @param {Object} config.initialFormData - Default form values for reset
 * @param {string} config.entityName - Display name for toast messages (e.g., "Business")
 * @param {Function} [config.loadReferenceData] - Async function to load dropdowns/reference data
 * @param {Function} [config.mapEditData] - Transform item data when populating edit form
 * @param {Function} [config.transformSubmitData] - Transform form data before API submission
 * @param {Function} [config.validateSubmitData] - Validate form data, return error string or null
 * @param {boolean} [config.useConfirmModal=true] - Whether to show confirm modal on delete
 * @param {Function} [config.getItemDisplayName] - Get display name for confirm modal (item) => string
 * @param {Function} [config.onUpdate] - Callback after any successful create/update/delete/toggle
 * @returns {Object} State and handlers for CRUD operations
 *
 * @example
 * const { items, formData, setFormData, handleSubmit, handleEdit, handleDelete } = useFormCRUD({
 *   apiGetAll: () => api.getBusinesses({ include_inactive: true }),
 *   apiCreate: api.createBusiness,
 *   apiUpdate: api.updateBusiness,
 *   apiDelete: api.deleteBusiness,
 *   initialFormData: { name: '' },
 *   entityName: 'Business',
 * });
 */
function useFormCRUD(config) {
  const {
    initialFormData,
    entityName,
    useConfirmModal = true,
  } = config;

  // Store config functions in a ref so effects/callbacks don't re-trigger
  // when inline arrow functions create new references each render.
  const configRef = useRef(config);
  configRef.current = config;

  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [referenceData, setReferenceData] = useState({});

  /**
   * Fetch all items from the API and update state.
   */
  const loadItems = useCallback(async () => {
    try {
      const response = await configRef.current.apiGetAll();
      setItems(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  // Load items on mount
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Load reference data on mount
  useEffect(() => {
    const loadRefDataFn = configRef.current.loadReferenceData;
    if (loadRefDataFn) {
      loadRefDataFn().then((data) => {
        setReferenceData(data || {});
      }).catch((error) => {
        console.error('Error loading reference data:', error);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle form submission for both creating and updating items.
   * Validates, transforms, and submits data, then resets form and reloads.
   *
   * @param {Event} e - Form submit event
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;

    const cfg = configRef.current;

    if (cfg.validateSubmitData) {
      const error = cfg.validateSubmitData(formData);
      if (error) {
        setToast({ message: error, type: 'error' });
        return;
      }
    }

    setSubmitting(true);
    try {
      const submitData = cfg.transformSubmitData ? cfg.transformSubmitData(formData) : formData;

      if (editingId) {
        await cfg.apiUpdate(editingId, submitData);
        setToast({ message: `${entityName} updated successfully!`, type: 'success' });
        setEditingId(null);
      } else {
        await cfg.apiCreate(submitData);
        setToast({ message: `${entityName} added successfully!`, type: 'success' });
      }

      setFormData(initialFormData);
      loadItems();
      if (cfg.onUpdate) cfg.onUpdate();
    } catch (error) {
      setToast({
        message: error.response?.data?.error || `Error saving ${entityName}`,
        type: 'error',
      });
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, formData, editingId, entityName, initialFormData, loadItems]);

  /**
   * Populate the form with an existing item's data for editing.
   * Applies mapEditData transform if provided, then scrolls to top.
   *
   * @param {Object} item - The item object to edit
   */
  const handleEdit = useCallback((item) => {
    const mapFn = configRef.current.mapEditData;
    const mapped = mapFn ? mapFn(item) : { ...item };
    setFormData(mapped);
    setEditingId(item.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /**
   * Cancel editing and reset the form to its initial state.
   */
  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setFormData(initialFormData);
  }, [initialFormData]);

  /**
   * Delete an item, optionally showing a confirm modal first.
   * On success: reloads items, shows toast, and calls onUpdate.
   *
   * @param {number} id - The ID of the item to delete
   * @param {string} [displayName] - Display name for the confirmation dialog
   */
  const handleDelete = useCallback((id, displayName) => {
    const performDelete = async () => {
      setConfirmModal(null);
      try {
        await configRef.current.apiDelete(id);
        setEditingId((currentEditingId) => {
          if (currentEditingId === id) {
            setFormData(initialFormData);
            return null;
          }
          return currentEditingId;
        });
        loadItems();
        if (configRef.current.onUpdate) configRef.current.onUpdate();
        setToast({ message: `${entityName} deleted`, type: 'success' });
      } catch (error) {
        setToast({
          message: error.response?.data?.error || `Error deleting ${entityName}`,
          type: 'error',
        });
        console.error('Error:', error);
      }
    };

    if (useConfirmModal) {
      setConfirmModal({
        title: `Delete ${entityName}`,
        message: `Delete ${entityName.toLowerCase()} "${displayName || ''}"?`,
        confirmLabel: 'Delete',
        variant: 'danger',
        onConfirm: performDelete,
      });
    } else {
      performDelete();
    }
  }, [initialFormData, loadItems, entityName, useConfirmModal]);

  /**
   * Toggle an item between active and inactive status.
   * On success: reloads items and calls onUpdate.
   *
   * @param {number} id - The ID of the item to toggle
   */
  const handleToggleStatus = useCallback(async (id) => {
    const toggleFn = configRef.current.apiToggleStatus;
    if (!toggleFn) return;
    try {
      await toggleFn(id);
      loadItems();
      if (configRef.current.onUpdate) configRef.current.onUpdate();
    } catch (error) {
      setToast({
        message: error.response?.data?.error || 'Error toggling status',
        type: 'error',
      });
      console.error('Error:', error);
    }
  }, [loadItems]);

  /**
   * Dismiss the toast notification.
   */
  const handleCloseToast = useCallback(() => {
    setToast(null);
  }, []);

  /**
   * Dismiss the confirm modal.
   */
  const handleCloseConfirmModal = useCallback(() => {
    setConfirmModal(null);
  }, []);

  return {
    items,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    confirmModal,
    referenceData,
    setToast,
    loadItems,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseToast,
    handleCloseConfirmModal,
  };
}

export default useFormCRUD;
